import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";

import { db } from "../firebase";
import { withStoreBalance } from "../utils/itemLedgerBalance";

/* =================================================
   DETERMINISTIC TRANSACTION SORTER
================================================= */

const getDeterministicSortedTransactions = (
  docs = []
) => {

  return [...docs].sort((a, b) => {

    const aData =
      a.data();

    const bData =
      b.data();

    // =========================================
    // PRIMARY SORT
    // created_at seconds ASC
    // =========================================

    const aSeconds =
      aData?.created_at?.seconds || 0;

    const bSeconds =
      bData?.created_at?.seconds || 0;

    if (aSeconds !== bSeconds) {

      return aSeconds - bSeconds;
    }

    // =========================================
    // SECONDARY SORT
    // nanoseconds ASC
    // =========================================

    const aNano =
      aData?.created_at?.nanoseconds || 0;

    const bNano =
      bData?.created_at?.nanoseconds || 0;

    if (aNano !== bNano) {

      return aNano - bNano;
    }

    // =========================================
    // TERTIARY SORT
    // movement_type ASC
    // =========================================

    const aMovement =
      String(
        aData?.movement_type || ""
      ).trim();

    const bMovement =
      String(
        bData?.movement_type || ""
      ).trim();

    if (aMovement !== bMovement) {

      return aMovement.localeCompare(
        bMovement
      );
    }

    // =========================================
    // FINAL TIE BREAKER
    // document id ASC
    // =========================================

    return a.id.localeCompare(
      b.id
    );
  });
};

/* =================================================
   ROW NORMALIZER
================================================= */

const normalizeLedgerRows = (
  sortedDocs = []
) => {

  return sortedDocs.map((docSnap) => {

    const d =
      docSnap.data();

    const qty =
      Number(
        d.qty || 0
      );

    const stockEffect =
      Number(
        d.stock_effect || 0
      );

    return {

      id:
        docSnap.id,

      // =========================================
      // RAW DATA
      // =========================================

      ...d,

      // =========================================
      // NORMALIZED FIELDS
      // =========================================

      item_id:
        d.item_id || "",

      item_name:
        d.item_name || "",

      item_code:
        d.item_code || "",

      category:
        d.category || "",

      movement_type:
        d.movement_type || "",

      qty,

      stock_effect:
        stockEffect,

      // =========================================
      // CACHE FIELD
      // NOT SOURCE OF TRUTH
      // =========================================

      current_stock:
        Number(
          d.current_stock || 0
        ),

      unit:
        d.unit || "",

      site_id:
        d.site_id || "",

      site_name:
        d.site_name || "",

      project_id:
        d.project_id || "",

      project_name:
        d.project_name || "",

      technician_id:
        d.technician_id || "",

      technician_name:
        d.technician_name || "",

      location_type:
        d.location_type || "",

      location_id:
        d.location_id || "",

      reference_id:
        d.reference_id || "",

      reference_type:
        d.reference_type || "",

      issued_by:
        d.issued_by_name || d.issued_by || "",

      received_by:
        d.received_by_name || d.received_by || "",

      approved_by:
        d.approved_by || "",

      remarks:
        d.remarks || "",

      deterministic_key:
        d.deterministic_key || "",

      created_at:
        d.created_at || null
    };
  });
};

/* =================================================
   ITEM LEDGER SERVICE
================================================= */

export const getItemLedger = async (
  companyId,
  itemId
) => {

  try {

    // =========================================
    // VALIDATION
    // =========================================

    if (!companyId) {

      throw new Error(
        "companyId required"
      );
    }

    if (!itemId) {

      return [];
    }

    // =========================================
    // FETCH TRANSACTIONS
    // =========================================

    const q = query(

      collection(
        db,
        "inventory_transactions"
      ),

      where(
        "company_id",
        "==",
        companyId
      ),

      where(
        // P4-5: by item_id, never item_code — codes were shared between
        // items, and old ledger rows keep their old code after a rename.
        "item_id",
        "==",
        itemId
      )
    );

    const snap =
      await getDocs(q);

    // =========================================
    // EMPTY RESPONSE
    // =========================================

    if (snap.empty) {

      return [];
    }

    // =========================================
    // DETERMINISTIC SORT
    // oldest -> newest
    // =========================================

    const sortedDocs =
      getDeterministicSortedTransactions(
        snap.docs
      );

    const rows =
      normalizeLedgerRows(
        sortedDocs
      );

    // =========================================
    // UI RENDER ONLY
    // newest -> oldest, with the STORE balance
    // on each row (P4-5c — site rows never count)
    // =========================================

    return withStoreBalance(
      [...rows].reverse()
    );

  } catch (error) {

    console.error(
      "ITEM LEDGER ERROR",
      error
    );

    throw error;
  }
};